/**
 * @file ElementPlacementGroup.tsx
 * @description Группа «Расположение» любого элемента секции (phase19.16 Р2;
 * uiux-design-r2 §4; SB100): выравнивание [←][↔][→], отступ сверху и
 * снизу — исключение из ритма колонки,
 * «Показывать» Телефон / Планшет / ПК. Сводка у свёрнутой группы.
 * Р2а: `locked` — обязательный элемент шаблона товара: устройства
 * отмечены и неактивны с причиной (uiux-design-r2a §6).
 *
 * SB122 (uiux-settings-groups §4.3): группа-карточка `SettingsGroup
 * id="place"` внутри `SettingsGroups panel="element"`, свёрнута по
 * умолчанию (панель задаёт `defaultCollapsed`), сводка «по центру · обычно ·
 * все». У обязательного элемента SB34 — причина в сводке, под галочками
 * устройств — `REQUIRED_DEVICES_TEXT`; замка в заголовке нет (ревью партии
 * SB122: выравнивание и отступы правятся, заперты только устройства).
 * Замечание проверки по полям группы — ✕ в заголовке.
 *
 * SB120 этап 3а (uiux-sb120-stage3 §2.1): отступы сверху и снизу — числом px
 * (`ElementSpaceField`: подсказки [0][8][16][32][64] × плотность, строка
 * «на телефоне × доля · Своё ›»); «обычно» — нет ключа, ритм колонки.
 */

import type { SectionElement } from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { SettingsGroup } from "../../../ui/settings-groups";
import { Segmented } from "../../../site-view/Segmented";
import { DeviceVisibilityField } from "../fields/DeviceVisibilityField";
import { ElementSpaceField } from "./ElementSpaceField";
import { hideFromShow, showFromHide } from "../../../../lib/site-builder/devices";
import { placementSummary } from "../../../../lib/site-builder/element-groups";
import { REQUIRED_DEVICES_TEXT } from "../../../../lib/site-builder/product-element-meta";

/** Id группы в панели элемента (свёрнута по умолчанию). */
export const PLACEMENT_GROUP_ID = "place";

const ALIGN = [
  { value: "auto", label: "Как колонка" },
  { value: "left", label: "←", ariaLabel: "Слева" },
  { value: "center", label: "↔", ariaLabel: "По центру" },
  { value: "right", label: "→", ariaLabel: "Справа" },
];

const HINT = "Выравнивание, отступы вокруг элемента и где его показывать.";
/** Причина замка в сводке свёрнутой группы (SB34): устройства не снимаются. */
export const LOCKED_SUMMARY = "обязательный, виден всегда";

interface Props {
  element: SectionElement;
  onChange: (element: SectionElement) => void;
  /** Обязательный элемент шаблона товара (SB34): устройства не снимаются. */
  locked?: boolean;
  /** Есть замечание проверки по полям группы. */
  invalid?: boolean;
  /** Путь элемента — живой предпросмотр отступов. */
  path: string;
  /** Ритм колонки на ПК, плотность магазина и доля телефона темы. */
  spacing: { rhythm: number; density: number; phoneFactor: number };
}

export function ElementPlacementGroup({ element, onChange, locked = false, invalid = false, path, spacing }: Props) {
  const show = showFromHide(element.hide);
  const setSpace = (space: SectionElement["space"]) => {
    const next = { ...element };
    if (space) next.space = space;
    else delete next.space;
    onChange(next);
  };
  const space = (side: "top" | "bottom", label: string) => (
    <ElementSpaceField side={side} label={label} space={element.space} onChange={setSpace} path={path} {...spacing} />
  );
  return (
    <SettingsGroup
      id={PLACEMENT_GROUP_ID}
      title="Расположение"
      summary={locked ? `${placementSummary(element)} · ${LOCKED_SUMMARY}` : placementSummary(element)}
      hint={HINT}
      status={invalid ? "error" : undefined}
    >
      <div className="flex flex-col gap-2">
        <Label>Выравнивание</Label>
        <Segmented aria-label="Выравнивание элемента" options={ALIGN} value={element.align ?? "auto"} onChange={(v) => onChange({ ...element, align: v === "auto" ? undefined : (v as "left" | "center" | "right") })} />
      </div>
      {space("top", "Отступ сверху")}
      {space("bottom", "Отступ снизу")}
      <DeviceVisibilityField value={show} onChange={(next) => onChange({ ...element, hide: hideFromShow(next) })} lockedReason={locked ? REQUIRED_DEVICES_TEXT : null} />
    </SettingsGroup>
  );
}
